import ResizablePanel from 'components/Panel/ResizablePanel';
import InlineRenamableTitle from 'components/Widget/InlineRenamableTitle';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { setActiveLeftPanelTab, setLeftPanelWidth } from 'redux/mode/actions';
import { LeftPanelTab } from 'redux/mode/reducers';
import { selectActiveScene } from 'redux/db/reducers';
import { canEdit } from 'redux/db/util';
import { trackSceneAction } from 'analytics';
import { executeCommand } from 'redux/mode/commandManager';
import { UpdateSceneCommand } from 'redux/mode/commands';
import { selectIsGuest } from 'redux/db/reducers';
import { RootReducer } from 'redux/reducers';
import { layout } from 'tokens';
import * as Base from '../styles';
import DesignSystem from './DesignSystem';
import TabBar from './TabBar';
import * as S from './styles';
import SceneChatPanel from '../Chat/ChatPanel/SceneChatPanel';

const LeftPanel = () => {
	const dispatch = useDispatch();
	const ref = useRef() as React.MutableRefObject<HTMLDivElement>;
	const { leftPanelWidths, activeLeftPanelTab, activeScene, sidebarDocked, isGuest } = useSelector(
		(r: RootReducer) => {
			return {
				leftPanelWidths: r.mode.leftPanelWidths,
				activeLeftPanelTab: r.mode.activeLeftPanelTab,
				activeScene: selectActiveScene(r.db, r.mode.activeSceneId),
				sidebarDocked: r.mode.sidebarDocked,
				isGuest: selectIsGuest(r.db),
			};
		},
		shallowEqual
	);

	const shouldShowDesignSystemTab = useMemo(() => {
		return !!activeScene?.metadata?.styles?.length;
	}, [activeScene?.metadata?.styles]);

	const availableTabs = useMemo(() => {
		if (!shouldShowDesignSystemTab) return [];
		return [LeftPanelTab.CHAT, LeftPanelTab.DESIGN_SYSTEM];
	}, [shouldShowDesignSystemTab]);

	// Normalize the active tab at render time. Redux holds the persisted value,
	// but it might be stale (unknown tab after a deploy, or DESIGN_SYSTEM when
	// the current scene has no DS). The repair dispatch below runs in an effect
	// *after* the first render, so we'd otherwise index leftPanelWidths with a
	// bad key for one frame. validTab always resolves to a tab we can render.
	const validTab = useMemo(() => {
		const validTabs = Object.values(LeftPanelTab) as string[];
		if (!validTabs.includes(activeLeftPanelTab)) return LeftPanelTab.CHAT;
		if (activeLeftPanelTab === LeftPanelTab.DESIGN_SYSTEM && !shouldShowDesignSystemTab) {
			return LeftPanelTab.CHAT;
		}
		return activeLeftPanelTab;
	}, [activeLeftPanelTab, shouldShowDesignSystemTab]);

	useEffect(() => {
		if (validTab !== activeLeftPanelTab) {
			dispatch(setActiveLeftPanelTab(validTab));
		}
	}, [validTab, activeLeftPanelTab, dispatch]);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------
	const onSceneTitleCommit = useCallback(
		(trimmed: string) => {
			if (!activeScene || trimmed === activeScene.title) return;
			trackSceneAction('rename');
			executeCommand(
				new UpdateSceneCommand(
					{ id: activeScene.id, title: activeScene.title },
					{ id: activeScene.id, title: trimmed }
				)
			);
		},
		[activeScene]
	);

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------
	const sceneTitle = activeScene?.title ?? 'Untitled Scene';
	const makeSpaceForSidebar = !isGuest && !sidebarDocked;

	return (
		<ResizablePanel
			resizeOnSide="right"
			size={leftPanelWidths[validTab]}
			onResize={(w) => {
				dispatch(setLeftPanelWidth({ tab: validTab, width: w }));
			}}
		>
			<Base.Base ref={ref}>
				<S.Top>
					<S.SceneNameRow
						makeSpaceForSidebar={makeSpaceForSidebar}
						sidebarToggleWidth={layout.sidebarToggleWidth}
					>
						<InlineRenamableTitle
							variant="panelTitle"
							value={sceneTitle}
							editable={!!activeScene && canEdit(activeScene)}
							onCommit={onSceneTitleCommit}
							title={sceneTitle}
						/>
					</S.SceneNameRow>
					{availableTabs.length > 0 && (
						<TabBar
							labels={availableTabs}
							activeLabel={validTab}
							onChange={(tab) => dispatch(setActiveLeftPanelTab(tab as LeftPanelTab))}
						/>
					)}
				</S.Top>
				<Base.Divider />
				{validTab === LeftPanelTab.CHAT && <SceneChatPanel />}
				{validTab === LeftPanelTab.DESIGN_SYSTEM && shouldShowDesignSystemTab && (
					<DesignSystem />
				)}
			</Base.Base>
		</ResizablePanel>
	);
};

export default LeftPanel;
